<template>
  <div
    class="tab-pane fade"
    :class="{ 'show active': isActive }"
    id="nav-event-highlights"
    role="tabpanel"
    aria-labelledby="nav-event-highlights-tab"
  >
    <div class="event-highlights">
      <ul
        id="highlights-pills-tab"
        class="nav nav-pills highlights-tabs"
        role="tablist"
      >
        <li class="nav-item">
          <a
            id="highlights-tab"
            class="nav-link active"
            data-toggle="pill"
            href="#highlights-pane"
            role="tab"
            aria-controls="highlights-pane"
            aria-selected="true"
          >
            Highlights ({{ highlights.total }})
          </a>
        </li>
        <li class="nav-item">
          <a
            id="purchased-tab"
            class="nav-link"
            data-toggle="pill"
            href="#purchased-pane"
            role="tab"
            aria-controls="purchased-pane"
            aria-selected="false"
            @click="loadPurchased"
          >
            Purchased ({{ purchased.total }})
          </a>
        </li>
      </ul>

      <div class="tab-content highlights-tab-content">
        <div
          id="highlights-pane"
          class="tab-pane fade show active"
          role="tabpanel"
          aria-labelledby="highlights-tab"
        >
          <p class="highlights-note">
            Note: Each video can be downloaded within {{ expiryDays }} days of
            the activity upload date. Once purchased and downloaded, they remain
            in your library for {{ expiryDays }} days from the purchase date.
          </p>
          <div class="row" v-if="highlights.items.length">
            <div
              class="col-12 col-sm-6 col-md-4"
              v-for="(video, i) of highlights.items"
              :key="i"
            >
              <highlight-card
                :video="video"
                @play="openPlayer"
                @download="confirmPurchase"
              />
            </div>
          </div>
          <p v-else class="empty-state">No highlights available.</p>
          <div v-if="highlights.hasMore" class="hl-load-more">
            <button
              type="button"
              class="hl-btn hl-btn--cancel"
              :disabled="highlights.loadingMore"
              @click="loadMoreHighlights"
            >
              <span>{{ highlights.loadingMore ? "Loading…" : "Load More" }}</span>
            </button>
          </div>
        </div>

        <div
          id="purchased-pane"
          class="tab-pane fade"
          role="tabpanel"
          aria-labelledby="purchased-tab"
        >
          <p class="highlights-note">
            Note: Each purchased video remains in your library for
            {{ expiryDays }} days from the purchase date.
          </p>
          <div class="row" v-if="purchased.items.length">
            <div
              class="col-12 col-sm-6 col-md-4"
              v-for="(video, i) of purchased.items"
              :key="i"
            >
              <highlight-card
                :video="video"
                purchased
                @play="openPlayer"
                @download="confirmRedownload"
                @delete="confirmDelete"
              />
            </div>
          </div>
          <p v-else class="empty-state">No purchased videos.</p>
          <div v-if="purchased.hasMore" class="hl-load-more">
            <button
              type="button"
              class="hl-btn hl-btn--cancel"
              :disabled="purchased.loadingMore"
              @click="loadMorePurchased"
            >
              <span>{{ purchased.loadingMore ? "Loading…" : "Load More" }}</span>
            </button>
          </div>
        </div>
      </div>

      <highlight-video-player
        v-if="activeVideo"
        :video="activeVideo"
        :purchased="activeVideoPurchased"
        @close="activeVideo = null"
        @download="playerDownload"
      />

      <highlight-dialog
        v-if="purchaseVideo"
        title="Download Video"
        @close="purchaseVideo = null"
      >
        <p class="hl-confirm-text">
          Downloading this video will cost {{ purchaseVideo.points }} Gems.<br />
          Do you want to continue?
        </p>
        <div class="hl-confirm-box">
          <div class="hl-confirm-cost">
            <img src="/assets/event-highlights/gems.svg" alt="" />
            <span>{{ purchaseVideo.points }}</span>
          </div>
          <div v-if="canAfford" class="hl-confirm-balance">
            Gems Balance: {{ gems }}
          </div>
          <div v-else class="hl-confirm-balance is-error">
            <i class="fas fa-exclamation-circle"></i>
            Insufficient Gems Balance: {{ gems }}
          </div>
        </div>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="purchaseVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button
            v-if="canAfford"
            type="button"
            class="hl-btn hl-btn--download"
            @click="doPurchase"
          >
            <span>Download</span>
          </button>
          <button
            v-else
            type="button"
            class="hl-btn hl-btn--buygems"
            @click="buyGems"
          >
            <span>Buy Gems</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-dialog
        v-if="redownloadVideo"
        title="Download Video"
        @close="redownloadVideo = null"
      >
        <p class="hl-confirm-text">
          Are you sure you want to download this video
        </p>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="redownloadVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button
            type="button"
            class="hl-btn hl-btn--download"
            @click="doRedownload"
          >
            <span>Download</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-dialog
        v-if="deleteVideo"
        title="Delete Video"
        @close="deleteVideo = null"
      >
        <p class="hl-confirm-text">
          Are you sure you want to delete this video
        </p>
        <template #actions>
          <button
            type="button"
            class="hl-btn hl-btn--cancel"
            @click="deleteVideo = null"
          >
            <span>Cancel</span>
          </button>
          <button type="button" class="hl-btn hl-btn--danger" @click="doDelete">
            <span>Delete</span>
          </button>
        </template>
      </highlight-dialog>

      <highlight-download-dialog
        v-if="downloadingVideo"
        :video="downloadingVideo"
        :progress="downloadProgress"
        :downloaded-bytes="downloadedBytes"
        :total-bytes="totalBytes"
        :state="downloadDialogState"
        @close="closeDownloading"
      />
    </div>
  </div>
</template>

<script>
import HighlightCard from "./HighlightCard";
import HighlightDialog from "./HighlightDialog";
import HighlightDownloadDialog from "./HighlightDownloadDialog";
import HighlightVideoPlayer from "./HighlightVideoPlayer";
import {
  mapHighlight,
  mapPurchase,
  pageItems,
  pageHasMore,
  pageCurrent,
  pageExpiryDays,
  pageTotal,
  pagePurchasedTotal,
} from "@/utils/eventHighlights";
import "@/assets/css/event-highlights.css";

function emptyList() {
  return {
    items: [],
    page: 1,
    total: 0,
    hasMore: false,
    loaded: false,
    loading: false,
    loadingMore: false,
  };
}

export default {
  name: "EventHighlights",
  components: {
    HighlightCard,
    HighlightDialog,
    HighlightDownloadDialog,
    HighlightVideoPlayer,
  },
  props: {
    isActive: { type: Boolean, default: false },
    gemsBalance: { type: Number, default: 0 },
    eventId: { type: String, default: "" },
  },
  emits: ["buy-gems"],
  data() {
    return {
      activeVideo: null,
      purchaseVideo: null,
      redownloadVideo: null,
      deleteVideo: null,
      downloadingVideo: null,
      downloadProgress: 0,
      downloadedBytes: 0,
      totalBytes: 0,
      downloadError: false,
      downloadRequest: null,
      downloadToken: 0,
      gems: this.gemsBalance,
      highlights: emptyList(),
      purchased: emptyList(),
      expiryDays: 30,
    };
  },
  computed: {
    sources() {
      return {
        highlights: {
          list: this.highlights,
          action: "user/event_highlights",
          map: mapHighlight,
          params: { eventId: this.eventId },
        },
        purchased: {
          list: this.purchased,
          action: "user/event_highlights_purchased",
          map: mapPurchase,
          params: {},
        },
      };
    },
    activeVideoPurchased() {
      if (!this.activeVideo) return false;
      return this.purchased.items.indexOf(this.activeVideo) !== -1;
    },
    canAfford() {
      if (!this.purchaseVideo) return false;
      return this.gems >= this.purchaseVideo.points;
    },
    overlayOpen() {
      return (
        !!this.activeVideo ||
        !!this.purchaseVideo ||
        !!this.redownloadVideo ||
        !!this.deleteVideo ||
        !!this.downloadingVideo
      );
    },
    downloadDialogState() {
      if (this.downloadError) return "error";
      if (this.downloadProgress >= 100) return "complete";
      return "downloading";
    },
  },
  watch: {
    isActive(active) {
      if (active) this.loadHighlights();
    },
    gemsBalance(val) {
      this.gems = val;
    },
    overlayOpen(open) {
      document.body.style.overflow = open ? "hidden" : "";
    },
  },
  async mounted() {
    if (this.isActive) await this.loadHighlights();
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
  methods: {
    loadHighlights() {
      return this.loadFirstPage(this.sources.highlights);
    },
    loadPurchased() {
      return this.loadFirstPage(this.sources.purchased);
    },
    loadMoreHighlights() {
      return this.loadNextPage(this.sources.highlights);
    },
    loadMorePurchased() {
      return this.loadNextPage(this.sources.purchased);
    },
    async loadFirstPage(source) {
      const list = source.list;
      if (list.loaded || list.loading) return;
      list.loading = true;

      try {
        const res = await this.$store.dispatch(source.action, {
          page: 1,
          ...source.params,
        });

        list.items = pageItems(res).map(source.map);
        list.page = pageCurrent(res);
        list.hasMore = pageHasMore(res);
        list.total = pageTotal(res);
        list.loaded = true;

        this.readListMeta(res);
      } finally {
        list.loading = false;
      }
    },
    async loadNextPage(source) {
      const list = source.list;
      if (list.loadingMore || !list.hasMore) return;
      list.loadingMore = true;

      const nextPage = list.page + 1;

      try {
        const res = await this.$store.dispatch(source.action, {
          page: nextPage,
          ...source.params,
        });

        if (list.page !== nextPage - 1) return;

        list.items.push(...pageItems(res).map(source.map));
        list.page = pageCurrent(res);
        list.hasMore = pageHasMore(res);
      } finally {
        list.loadingMore = false;
      }
    },
    readListMeta(res) {
      const expiryDays = pageExpiryDays(res);
      if (expiryDays) this.expiryDays = expiryDays;

      const purchasedTotal = pagePurchasedTotal(res);
      if (purchasedTotal) this.purchased.total = purchasedTotal;
    },
    openPlayer(video) {
      this.activeVideo = video;
    },
    playerDownload(video) {
      this.activeVideoPurchased ? this.confirmRedownload(video) : this.confirmPurchase(video);
    },
    confirmPurchase(video) {
      this.purchaseVideo = video;
      this.$store.dispatch("user/get_player_wallet");
    },
    async doPurchase() {
      if (!this.purchaseVideo) return;
      if (!this.canAfford) return;
      const video = this.purchaseVideo;

      this.purchaseVideo = null;
      this.activeVideo = null;

      let result = null;
      try {
        result = await this.$store.dispatch(
          "user/event_highlights_download",
          { highlightId: video.highlightId, videoType: "purchase" }
        );
      } catch (e) {
        return;
      }

      const purchaseDoc = result?.purchase ?? null;
      if (!purchaseDoc) {
        return;
      }

      const newGems = typeof result?.gems === "number" ? result.gems : null;

      if (newGems !== null) {
        this.gems = newGems;
      } else {
        this.gems -= video.points;
      }

      this.$store.dispatch("user/get_player_wallet");

      const idx = this.highlights.items.indexOf(video);
      if (idx !== -1) this.highlights.items.splice(idx, 1);
      this.highlights.total = Math.max(0, this.highlights.total - 1);
      this.purchased.total += 1;
      const purchasedVideo = mapPurchase(purchaseDoc);
      if (this.purchased.loaded) this.purchased.items.push(purchasedVideo);

      await this.startDownload(await this.withDownloadUrl(purchasedVideo));
    },
    confirmRedownload(video) {
      this.redownloadVideo = video;
    },
    async doRedownload() {
      if (!this.redownloadVideo) return;
      const video = await this.withDownloadUrl(this.redownloadVideo);
      if (!video) return;

      this.redownloadVideo = null;
      this.activeVideo = null;
      await this.startDownload(video);
    },
    async withDownloadUrl(video) {
      try {
        const result = await this.$store.dispatch(
          "user/event_highlights_download",
          { highlightId: video.highlightId, videoType: "download" }
        );
        return result?.downloadUrl ? { ...video, downloadUrl: result.downloadUrl } : null;
      } catch (e) {
        return null;
      }
    },
    cancelDownload() {
      if (this.downloadRequest) this.downloadRequest.abort();
      this.downloadRequest = null;
      this.downloadToken += 1;
    },
    async startDownload(video, isRetry = false) {
      if (!video || !video.downloadUrl) return;

      this.cancelDownload();
      const request = new AbortController();
      const token = this.downloadToken;
      this.downloadRequest = request;

      this.downloadingVideo = video;
      this.downloadProgress = 0;
      this.downloadedBytes = 0;
      this.totalBytes = 0;
      this.downloadError = false;

      try {
        const resp = await fetch(video.downloadUrl, { signal: request.signal });
        if (!resp.ok) throw new Error("HTTP " + resp.status);

        const total = Number(resp.headers.get("Content-Length")) || 0;
        this.totalBytes = total;
        const reader = resp.body && resp.body.getReader && resp.body.getReader();

        let blob;
        if (reader) {
          const chunks = [];
          let received = 0;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (token !== this.downloadToken) return;
            chunks.push(value);
            received += value.length;
            this.downloadedBytes = received;
            if (total > 0) {
              this.downloadProgress = Math.min(
                99,
                Math.round((received / total) * 100)
              );
            }
          }
          blob = new Blob(chunks, {
            type: resp.headers.get("Content-Type") || "video/mp4",
          });
        } else {
          blob = await resp.blob();
          this.downloadedBytes = blob.size;
          if (!this.totalBytes) this.totalBytes = blob.size;
        }

        if (!this.totalBytes && this.downloadedBytes) {
          this.totalBytes = this.downloadedBytes;
        }
        if (token !== this.downloadToken) return;

        this.downloadProgress = 100;
        this.saveBlob(blob, video);
      } catch (e) {
        if (token !== this.downloadToken) return;

        const fresh = isRetry ? null : await this.withDownloadUrl(video);
        if (fresh) {
          await this.startDownload(fresh, true);
          return;
        }

        this.downloadError = true;
      }
    },
    closeDownloading() {
      this.cancelDownload();
      this.downloadingVideo = null;
      this.downloadProgress = 0;
      this.downloadedBytes = 0;
      this.totalBytes = 0;
      this.downloadError = false;
    },
    saveBlob(blob, video) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (video.title || "highlight") + ".mp4";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    buyGems() {
      this.purchaseVideo = null;
      this.$emit("buy-gems");
    },
    confirmDelete(video) {
      this.deleteVideo = video;
    },
    async doDelete() {
      if (!this.deleteVideo) return;
      const video = this.deleteVideo;
      this.deleteVideo = null;

      if (video.purchaseId) {
        try {
          await this.$store.dispatch(
            "user/event_highlights_delete_purchase",
            video.purchaseId
          );
        } catch (e) {
          return;
        }
      }

      const idx = this.purchased.items.indexOf(video);
      if (idx !== -1) this.purchased.items.splice(idx, 1);

      this.highlights.loaded = false;
      this.purchased.loaded = false;
      await Promise.all([this.loadHighlights(), this.loadPurchased()]);
    },
  },
};
</script>

<style scoped>
/* standalone panel on the player-activities page (no longer wrapped by the
   edit-profile .login-box, so it brings its own surface + spacing) */
#nav-event-highlights {
  padding: 0 0 40px;
}
.event-highlights {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
@media (max-width: 575.98px) {
  .event-highlights {
    padding: 16px;
  }
}
.highlights-tabs {
  background: #e8e8e8;
  border-radius: 10px;
  padding: 6px;
  /* keep the two pills from butting together — match the outer padding */
  gap: 6px;
  margin-bottom: 20px;
}
.highlights-tabs .nav-item {
  /* grow equally from a 0 basis so the 6px gap fits without wrapping —
     a 50% basis + gap overflows the row and stacks the pills */
  flex: 1 1 0;
  text-align: center;
}
.highlights-tabs .nav-link {
  color: #1a1a1a;
  font-weight: 700;
  font-size: 18px;
  border-radius: 8px;
  padding: 12px 0;
}
.highlights-tabs .nav-link.active {
  background: #000;
  color: #fff;
}
.highlights-note {
  background: #efefef;
  border-radius: 8px;
  text-align: center;
  padding: 14px;
  margin-bottom: 20px;
  font-size: 16px;
  color: #333;
}
.empty-state {
  text-align: center;
  padding: 30px;
  color: #818181;
}
.hl-load-more {
  display: flex;
  justify-content: center;
  margin: 12px 0 4px;
}
.hl-load-more .hl-btn {
  min-width: 180px;
}
</style>
